"use client";

import { Fragment } from "react";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { headingOn, onSurface, surfaceFrom } from "@/components/optilase/surface";
import { regionFromPathname } from "@/lib/region";
import { richParagraphs } from "@/lib/richtext";
import { cn } from "@/lib/utils";
import { usePublicPathname } from "@/lib/public-pathname";

type ComparisonRowBlok = SbBlokData & {
  label?: string;
  column_a?: unknown;
  column_b?: unknown;
};

type ComparisonTableBlok = SbBlokData & {
  heading?: string;
  intro?: string;
  column_a_label?: string;
  column_b_label?: string;
  rows?: ComparisonRowBlok[];
  bg_color?: unknown;
};

function Tick() {
  return (
    <span className="bg-teal-06 flex size-7 shrink-0 items-center justify-center rounded-full">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="/icons/compare-tick.svg" alt="Tick" aria-hidden width={13} height={10} />
    </span>
  );
}

function Cross() {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src="/icons/compare-cross.svg"
      alt="Cross"
      aria-hidden
      width={28}
      height={28}
      className="shrink-0"
    />
  );
}

export default function ComparisonTable({ blok }: { blok: ComparisonTableBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const surface = surfaceFrom(blok.bg_color);
  const rows = (blok.rows ?? []).filter((r) => r.label || r.column_a || r.column_b);
  if (rows.length === 0) return null;

  const a = blok.column_a_label || "";
  const b = blok.column_b_label || "";
  const cell = (value: unknown) => richParagraphs(value, region);

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 lg:px-20 lg:py-20", surface ? "" : "bg-white")}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 lg:gap-12">
        <div className="flex flex-col gap-5">
          <h2
            className={cn(
              "text-section text-center lg:text-left",
              headingOn(surface, "text-teal-07"),
            )}
          >
            {blok.heading}
          </h2>
          {blok.intro && (
            <p
              className={cn(
                "max-w-[943px] text-center text-base leading-relaxed tracking-[-0.01em] lg:text-left lg:text-xl",
                onSurface(surface, "text-white/85", "text-grey-08", "text-grey-08"),
              )}
            >
              {blok.intro}
            </p>
          )}
        </div>

        <div className="hidden grid-cols-[240px_minmax(0,1fr)_minmax(0,1fr)] lg:grid">
          <div />
          <div className="text-h5 text-grey-09 px-6 pt-5 pb-5 font-semibold">{a}</div>
          <div className="text-h5 bg-teal-02 text-grey-09 rounded-t-xl px-6 pt-5 pb-5 font-semibold">
            {b}
          </div>

          {rows.map((row, i) => {
            const last = i === rows.length - 1;
            return (
              <Fragment key={row._uid as string}>
                <div
                  {...storyblokEditable(row)}
                  className={cn(
                    "text-teal-08 py-4 pr-6 font-semibold",
                    last ? "" : "border-teal-03 border-b",
                  )}
                >
                  {row.label}
                </div>
                <div
                  className={cn("text-grey-07 px-6 py-4", last ? "" : "border-teal-03 border-b")}
                >
                  {cell(row.column_a).map((p, n) => (
                    <p key={n}>{p}</p>
                  ))}
                </div>
                <div
                  className={cn(
                    "bg-teal-02 text-grey-07 flex items-start gap-3 px-6 py-4",
                    last ? "rounded-b-xl" : "border-teal-03 border-b",
                  )}
                >
                  <Tick />
                  <div>
                    {cell(row.column_b).map((p, n) => (
                      <p key={n}>{p}</p>
                    ))}
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>

        <div className="flex flex-col gap-6 lg:hidden">
          <div className="border-teal-04 flex items-center gap-5 border-y py-2">
            <p className="text-h6 text-grey-08 flex-1 text-center font-semibold">{a}</p>
            <span aria-hidden className="bg-teal-04 h-13 w-px shrink-0" />
            <p className="text-h6 text-grey-08 flex-1 text-center font-semibold">{b}</p>
          </div>

          {rows.map((row) => (
            <div
              key={row._uid as string}
              {...storyblokEditable(row)}
              className="flex flex-col gap-2"
            >
              <p className="text-teal-08 text-center text-xl font-semibold md:text-2xl">
                {row.label}
              </p>
              <div className="flex items-stretch">
                <div className="bg-grey-01 text-grey-07 flex flex-1 flex-col items-center gap-2 rounded-l-xl p-4 text-center md:gap-3 md:p-6">
                  <Cross />
                  {cell(row.column_a).map((p, n) => (
                    <p key={n}>{p}</p>
                  ))}
                </div>
                <div className="bg-teal-02 text-grey-07 flex flex-1 flex-col items-center gap-2 rounded-r-xl px-3 py-4 text-center md:gap-3 md:p-6">
                  <Tick />
                  {cell(row.column_b).map((p, n) => (
                    <p key={n}>{p}</p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
